/**
 * ToolsContext — Session-scoped state that binds the vehicle-detail
 * financing calculator and trade-in estimator together.
 *
 * When a customer completes a trade-in estimate on a vehicle page, the
 * financing widget picks it up via `activeTradeIn` and adds the mid value
 * as a "trade-in credit" that reduces the loan principal. The chip can be
 * dismissed to remove the credit.
 *
 * We deliberately DON'T persist to localStorage — the sync is meant to be
 * ephemeral per browsing session. If the user reloads or navigates away,
 * they can always re-fetch their saved estimates from /account/trade-ins.
 */
import React, { createContext, useContext, useMemo, useState } from "react";

const ToolsContext = createContext(null);

export const ToolsProvider = ({ children }) => {
  const [activeTradeIn, setActiveTradeIn] = useState(null);

  const value = useMemo(() => ({
    activeTradeIn,
    setActiveTradeIn,
    clearTradeIn: () => setActiveTradeIn(null),
    // Convenience: cents value the financing widget can consume.
    tradeInCreditCents: activeTradeIn ? activeTradeIn.estimate_mid_cents : 0,
  }), [activeTradeIn]);

  return <ToolsContext.Provider value={value}>{children}</ToolsContext.Provider>;
};

export const useTools = () => {
  const ctx = useContext(ToolsContext);
  if (!ctx) {
    // Return a no-op shape so components that render outside the provider
    // (e.g. server-side, tests) don't crash. In the app, VehicleDetail.jsx
    // is the only mount site.
    return {
      activeTradeIn: null,
      setActiveTradeIn: () => {},
      clearTradeIn: () => {},
      tradeInCreditCents: 0,
    };
  }
  return ctx;
};

export default ToolsContext;
